import type React from 'react';
import { Flex, Text, IconButton, Icon } from '@nula/core-ui';
import type { TranscriptSegmentData } from '@/lib/recordings/parse-transcript';
import type { SpeakerLabel } from './types/types';
import { SpeakerAvatar } from './speaker-avatar';
import { formatElapsed } from './elapsed';
import { resolveSpeakerName } from './resolve-speaker-name';
import { useSpeakerLabelRenderer } from './speaker-label-context';
import styles from './transcript-turn.module.css';

export interface TranscriptTurnProps {
  segment: TranscriptSegmentData;
  /** False for a consecutive turn by the same speaker — avatar + name are hidden. */
  showAvatar: boolean;
  isPlaying: boolean;
  /** Called with the segment start time (ms) when the row is clicked. */
  onSeek: (startMs: number) => void;
  /** When set (with segment.speaker_id), the name becomes an editable label. */
  recordingId?: string;
  calendarEventId?: string | null;
  /** Entity the attendees live on (passed to the speaker-label editor). */
  entityId?: string | null;
  /** This recording's audio surface (passed to the speaker-label editor to
   * scope the attendee roster). */
  surfaceId?: string | null;
  /** Editable speaker-label overrides — applied to the avatar + name. */
  speakerLabels?: Record<string, SpeakerLabel>;
  /** Live attendee names by (lowercased) email. */
  attendeeNameByEmail?: Record<string, string>;
  /** Live attendee profile photos by (lowercased) email. */
  attendeePhotoByEmail?: Record<string, string>;
  /** True while the attendees list is still loading — drives the resolving
   * skeleton so a labeled speaker doesn't flash "Speaker {id}". */
  attendeesLoading?: boolean;
  className?: string;
}

/**
 * One avatar-lead transcript turn. The whole row is a seek target (click →
 * onSeek(start_ms)). Consecutive turns by the same speaker pass
 * `showAvatar={false}` so they read as one continuous voice. Hovering reveals
 * a Copy action (copies the segment text; does not seek).
 */
export function TranscriptTurn({
  segment,
  showAvatar,
  isPlaying,
  onSeek,
  recordingId,
  calendarEventId,
  entityId,
  surfaceId,
  speakerLabels = {},
  attendeeNameByEmail,
  attendeePhotoByEmail,
  attendeesLoading,
  className,
}: TranscriptTurnProps) {
  const speaker = resolveSpeakerName(segment, speakerLabels, attendeeNameByEmail);
  // Profile photo for the labeled email, if that attendee has one.
  const labelEmail =
    segment.speaker_id != null ? speakerLabels[String(segment.speaker_id)]?.email : undefined;
  const photoUrl = labelEmail ? attendeePhotoByEmail?.[labelEmail.toLowerCase()] : undefined;
  // "Resolving": the speaker is labeled with an email that hasn't resolved to an
  // attendee yet AND attendees are still loading. Show skeletons instead of
  // flashing the bare "Speaker {id}" fallback, then snap to the real name/photo.
  const resolving =
    !!attendeesLoading &&
    !!labelEmail &&
    !attendeeNameByEmail?.[labelEmail.toLowerCase()];
  const timestamp = formatElapsed(segment.start_ms);
  const renderLabel = useSpeakerLabelRenderer();
  const editable = renderLabel != null && recordingId != null && segment.speaker_id != null;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard?.writeText(segment.text);
  };

  return (
    <Flex
      gap="sm"
      data-testid="transcript-turn"
      data-playing={isPlaying ? 'true' : 'false'}
      onClick={() => onSeek(segment.start_ms)}
      className={className ? `${styles.turn} ${className}` : styles.turn}
    >
      <Flex className={styles.gutter}>
        {showAvatar && <SpeakerAvatar speaker={speaker} photoUrl={photoUrl} loading={resolving} />}
      </Flex>

      <Flex vertical className={styles.body}>
        {showAvatar && (
          <Flex align="baseline" gap="xs" className={styles.meta}>
            {editable ? (
              <Flex
                className={styles.speakerSlot}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                {renderLabel!({
                  recordingId: recordingId!,
                  speakerId: segment.speaker_id!,
                  calendarEventId: calendarEventId ?? null,
                  entityId: entityId ?? null,
                  surfaceId: surfaceId ?? null,
                })}
              </Flex>
            ) : resolving ? (
              <Flex aria-hidden data-testid="speaker-name-loading" className={styles.nameLoading} />
            ) : (
              <Text className={styles.name}>{speaker}</Text>
            )}
            <Text className={styles.time}>
              {isPlaying && (
                <Icon name="play_arrow" size={9} className={styles.playIcon} aria-hidden />
              )}
              {timestamp}
            </Text>
          </Flex>
        )}
        {!showAvatar && (
          <Flex align="baseline" className={styles.meta}>
            <Text className={styles.time}>{timestamp}</Text>
          </Flex>
        )}
        <Text className={styles.text}>{segment.text}</Text>
      </Flex>

      <Flex className={styles.actions}>
        <IconButton
          aria-label="Copy"
          size="sm"
          transparent
          onClick={handleCopy}
        >
          <Icon name="content_copy" size={14} />
        </IconButton>
      </Flex>
    </Flex>
  );
}
